import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) setError("Reset token is missing from the link.");
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            setError("Passwords do not match");
            return;
        }
        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password updated", { description: "You can now sign in with your new password." });
            navigate("/");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex flex-col" data-testid="reset-password-page">
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-brand-600 mb-6"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="bg-white rounded-3xl shadow-soft border border-gray-100 p-8 md:p-10">
                        <div className="h-12 w-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center mb-5">
                            <Lock className="h-5 w-5" />
                        </div>
                        <h1 className="font-heading text-3xl font-semibold text-ink-900">
                            Set a new password
                        </h1>
                        <p className="mt-2 text-sm text-ink-700">
                            Choose a new password for your Aham Yog account.
                        </p>
                        <form onSubmit={onSubmit} className="mt-7 space-y-4">
                            <div>
                                <Label htmlFor="new-password">New password</Label>
                                <div className="relative mt-1.5">
                                    <Input
                                        id="new-password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        className="rounded-xl pr-10"
                                        data-testid="reset-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-700 hover:text-brand-600 focus:outline-none"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        aria-pressed={showPassword}
                                        data-testid="reset-password-toggle"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="confirm-password">Confirm password</Label>
                                <div className="relative mt-1.5">
                                    <Input
                                        id="confirm-password"
                                        type={showConfirm ? "text" : "password"}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        minLength={6}
                                        className="rounded-xl pr-10"
                                        data-testid="reset-confirm-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm((v) => !v)}
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-700 hover:text-brand-600 focus:outline-none"
                                        aria-label={showConfirm ? "Hide password" : "Show password"}
                                        aria-pressed={showConfirm}
                                        data-testid="reset-confirm-toggle"
                                        tabIndex={-1}
                                    >
                                        {showConfirm ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <div
                                    className="text-sm text-accent-700 bg-accent-50 px-3 py-2 rounded-lg"
                                    data-testid="reset-error"
                                >
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading || !token}
                                className="w-full rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta py-5 text-base"
                                data-testid="reset-submit-btn"
                            >
                                {loading ? "Updating..." : "Update password"}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
